import React, { useState } from 'react';

import { Box, Card, DropdownMenu, IconButton, Separator } from '@betterment/samba-react';
import { SambaAutoLink } from '@betterment/shared-components';
import { Button, Flex, TabularNumber, Text } from '@betterment/style-closet-react';
import { formatDistanceStrict, parseISO } from 'date-fns';

import { useCustomer } from './CustomerProvider';
import InvitationCancellationModal from './InvitationCancellationModal';

import { trackClick } from '../../features/track';
import { urlBuilder } from '../../Shared/urlBuilder';
import { Account, Invitation } from '../data';

interface Props {
  account: Account;
}

interface InviteExpirationTextProps {
  invitation: Invitation;
}

const InviteExpirationText: React.FC<React.PropsWithChildren<InviteExpirationTextProps>> = ({ invitation }) => {
  const timeLeft = formatDistanceStrict(parseISO(invitation.expiresAt), Date.now(), {
    unit: 'day',
  });
  let expirationText = `Expires in ${timeLeft}`;
  let isExpirationTextRed = false;

  if (timeLeft === '1 day') {
    isExpirationTextRed = true;
    expirationText = 'Expires tomorrow';
  } else if (timeLeft === '0 days') {
    isExpirationTextRed = true;
    expirationText = 'Expires at 11:59pm (ET)';
  }
  return <Text color={isExpirationTextRed ? 'coralAccessible' : 'inherit'}>{expirationText}</Text>;
};

const AccountDetails: React.FC<React.PropsWithChildren<Props>> = ({ account }) => {
  const { refetch } = useCustomer();
  const [shouldMaskAccountNumber, setShouldMaskAccountNumber] = useState(true);
  const [shouldShowInvitationCancellationModal, setShouldShowInvitationCancellationModal] = useState(false);
  const [targetInvitation, setTargetInvitation] = useState<Invitation>();
  const accountNumberLastFourDigits = account.accountNumber.substring(account.accountNumber.length - 4);

  const openCancelInvitationModal = (invitation: Invitation) => {
    trackClick({
      name: 'CancelJCInvite',
      module: 'CheckingSummary',
    });
    setTargetInvitation(invitation);
    setShouldShowInvitationCancellationModal(true);
  };

  return (
    <>
      <Card css={{ px: 'space.300', mb: 'space.200' }}>
        <Card.Header css={{ py: 'space.300' }}>
          <Text fontSize="3" fontWeight="medium" m="0">
            Account details
          </Text>
        </Card.Header>
        <Separator />
        <Card.Content css={{ py: 'space.300' }}>
          <Text as="p" aria-hidden={true} m="0">
            Routing number
          </Text>
          <TabularNumber aria-label="Routing number" fontSize="1" color="bmtGrey50" as="p" m="0" pb="6">
            {account.routingNumber}
          </TabularNumber>
          <Text as="p" aria-hidden={true} m="0">
            Account number
          </Text>
          <Flex
            justifyContent="space-between"
            alignItems="center"
            mb={account.jointUsersNames && account.jointUsersNames.length > 0 ? '6' : '0'}
          >
            {shouldMaskAccountNumber ? (
              <>
                <TabularNumber as="p" tabIndex={0} fontSize="1" m="0" color="bmtGrey50">
                  {`••••••••${accountNumberLastFourDigits}`}
                </TabularNumber>
                <Button
                  aria-label="Show account number"
                  variant="tertiary"
                  m="0"
                  p="0"
                  onClick={() => {
                    trackClick({ name: 'ShowAccountNumber' });
                    setShouldMaskAccountNumber(currentMaskState => !currentMaskState);
                  }}
                >
                  Show
                </Button>
              </>
            ) : (
              <>
                <TabularNumber as="p" tabIndex={0} fontSize="1" m="0" color="bmtGrey50">
                  {account.accountNumber}
                </TabularNumber>
                <Button
                  aria-label="Show account number"
                  variant="tertiary"
                  m="0"
                  p="0"
                  onClick={() => setShouldMaskAccountNumber(currentMaskState => !currentMaskState)}
                >
                  Hide
                </Button>
              </>
            )}
          </Flex>
          {account.jointUsersNames?.map(user => (
            <Text as="p" aria-hidden={true} m="0" key={`${user.firstName} ${user.lastName}`}>
              Joint with {user.firstName} {user.lastName}
            </Text>
          ))}
          {account.sentInvitations.map(invitation => (
            <Flex
              flexDirection="row"
              justifyContent="space-between"
              alignItems="center"
              pt="6"
              key={invitation.invitationId}
            >
              <Box>
                <Text as="p" m="0">
                  Pending invite to {invitation.receiverFirstName}
                </Text>
                <Text as="p" pb="1" color="bmtGrey50" fontSize="1">
                  <InviteExpirationText invitation={invitation} />
                </Text>
              </Box>
              <DropdownMenu modal={false}>
                <DropdownMenu.Trigger asChild={true}>
                  <IconButton name="Ellipses" label="invite options" variant="ghostSecondary" spacing="none" />
                </DropdownMenu.Trigger>
                <DropdownMenu.Content align="end">
                  <DropdownMenu.Item onSelect={() => openCancelInvitationModal(invitation)}>
                    Cancel invite
                  </DropdownMenu.Item>
                </DropdownMenu.Content>
              </DropdownMenu>
            </Flex>
          ))}
        </Card.Content>
        <Separator />
        <Card.Footer css={{ py: 'space.300' }}>
          <SambaAutoLink
            to={urlBuilder.getAppCheckingAccountDirectDeposit.absolute({ accountId: account.id })}
            onClick={() => trackClick({ name: 'SetupDirectDeposit', module: 'ManageCheckingAccount' })}
          >
            <Text fontSize="1" color="bmtBlue">
              Set up direct deposit
            </Text>
          </SambaAutoLink>
        </Card.Footer>
      </Card>

      {shouldShowInvitationCancellationModal && targetInvitation && (
        <InvitationCancellationModal
          invitation={targetInvitation}
          onClose={() => setShouldShowInvitationCancellationModal(false)}
          refreshCustomer={refetch}
        />
      )}
    </>
  );
};

export default AccountDetails;
